import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import jsPDF from "jspdf";
import { toast } from "react-toastify";
import {
  CheckCircle,
  AlertTriangle,
  Scale,
  Bug,
  Zap,
  XCircle,
  Info,
  Download,
  Loader2,
} from "lucide-react";
import {
  XAxis,
  YAxis,
  ResponsiveContainer,
  Legend,
  Cell,
  PieChart,
  Pie,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line,
} from "recharts";
import ScorerStat from "./ScorerStat";
import ScorerLegend from "./ScorerLegend";
import {
  renderActiveShape,
  renderActiveShapeCoverage,
  CustomTooltip,
} from "../../utils/chartHelpers";

export default function ReleaseScorer({ darkMode }) {
  const { selectedProject } = useSelector((state) => state.auth);

  const borderColor = darkMode ? "border-gray-800/50" : "border-gray-200";
  const cardBg = darkMode ? "bg-[#161922]" : "bg-white";

  const [activeBugIndex, setActiveBugIndex] = useState(0);
  const [activeCoverageIndex, setActiveCoverageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedSprint, setSelectedSprint] = useState("all");
  const [availableSprints, setAvailableSprints] = useState([]);

  const reportRef = useRef(null);
  const [blockingBugs, setBlockingBugs] = useState([]);
  const [failedTests, setFailedTests] = useState([]);

  // Dynamic Metrics State
  const [stats, setStats] = useState({
    readinessScore: 0,
    coverage: 0,
    openBugs: 0,
    successRate: 0,
    criticalIssues: 0,
    timeSaved: "0m",
    multiplier: 0,
    automationCalls: 0,
    totalTests: 0,
  });

  // Chart Data States
  const [releasePieData, setReleasePieData] = useState([]);
  const [coveragePieData, setCoveragePieData] = useState([]);
  const [trendData, setTrendData] = useState([]);

  const onPieEnter = (_, index, setter) => {
    setter(index);
  };

  useEffect(() => {
    const projectId = selectedProject?.project_id;
    if (!projectId) {
      setAvailableSprints([]);
      setSelectedSprint("all");
      return undefined;
    }

    let cancelled = false;
    setAvailableSprints([]);
    setSelectedSprint("all");

    const fetchSprints = async () => {
      try {
        const { data } = await axios.get(
          `https://api.testoraglobal.in/api/project/${projectId}/sprints`,
        );
        if (!cancelled) {
          setAvailableSprints(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Error fetching available sprints:", error);
        if (!cancelled) toast.error("Unable to load available sprints.");
      }
    };

    fetchSprints();
    return () => {
      cancelled = true;
    };
  }, [selectedProject?.project_id]);

  useEffect(() => {
    const projectId = selectedProject?.project_id;
    if (!projectId) {
      setStats((current) => ({ ...current, readinessScore: 0, totalTests: 0 }));
      setReleasePieData([]);
      setCoveragePieData([]);
      setTrendData([]);
      setBlockingBugs([]);
      setFailedTests([]);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const fetchMetrics = async () => {
      setIsLoading(true);
      try {
        const { data = {} } = await axios.get(
          `https://api.testoraglobal.in/api/risk/project/${projectId}/metrics?sprint=${encodeURIComponent(selectedSprint)}`,
        );
        if (cancelled) return;

        setStats((current) => ({ ...current, ...(data.stats || {}) }));
        setReleasePieData(Array.isArray(data.releasePieData) ? data.releasePieData : []);
        setCoveragePieData(Array.isArray(data.coveragePieData) ? data.coveragePieData : []);
        setTrendData(Array.isArray(data.trendData) ? data.trendData : []);
        setBlockingBugs(Array.isArray(data.blockingBugs) ? data.blockingBugs : []);
        setFailedTests(Array.isArray(data.failedTests) ? data.failedTests : []);
      } catch (error) {
        console.error("Error fetching release metrics:", error);
        if (!cancelled) toast.error("Unable to load release metrics.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchMetrics();
    return () => {
      cancelled = true;
    };
  }, [selectedProject?.project_id, selectedSprint]);

  const handleDownloadReport = async () => {
    if (!reportRef.current) return;

    setIsDownloading(true);
    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 16;
      const contentWidth = pageWidth - margin * 2;
      let cursorY = 20;

      const score = stats.readinessScore;
      const verdict = score >= 85
        ? { label: "APPROVED FOR RELEASE", color: [5, 150, 105] }
        : score >= 70
          ? { label: "REVIEW REQUIRED", color: [234, 125, 22] }
          : { label: "RELEASE BLOCKED", color: [220, 38, 38] };

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      pdf.setTextColor(17, 24, 39);
      pdf.text("Executive Quality Audit Report", margin, cursorY);
      cursorY += 7;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(107, 114, 128);
      pdf.text(
        `Project ${selectedProject?.project_id || "N/A"}  |  ${new Date().toLocaleDateString()}`,
        margin,
        cursorY,
      );
      cursorY += 9;

      pdf.setFillColor(...verdict.color);
      pdf.roundedRect(margin, cursorY, contentWidth, 19, 2, 2, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      pdf.setTextColor(255, 255, 255);
      pdf.text(verdict.label, margin + 5, cursorY + 8);
      pdf.setFontSize(10);
      pdf.text(`Release Readiness Score: ${score}/100`, margin + 5, cursorY + 14);
      cursorY += 27;

      const ensureSpace = (height) => {
        if (cursorY + height > pageHeight - margin) {
          pdf.addPage();
          cursorY = margin;
        }
      };

      const drawTable = (title, headers, rows, columnWidths) => {
        ensureSpace(18);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.setTextColor(31, 41, 55);
        pdf.text(title, margin, cursorY);
        cursorY += 5;

        const drawHeader = () => {
          let x = margin;
          headers.forEach((header, index) => {
            pdf.setFillColor(55, 65, 81);
            pdf.rect(x, cursorY, columnWidths[index], 8, "F");
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(8);
            pdf.setTextColor(255, 255, 255);
            pdf.text(String(header), x + 2, cursorY + 5.3);
            x += columnWidths[index];
          });
          cursorY += 8;
        };

        drawHeader();
        const tableRows = rows.length ? rows : [["-", "No records", ...headers.slice(2).map(() => "-")]];
        tableRows.forEach((row, rowIndex) => {
          const cellLines = row.map((cell, index) =>
            pdf.splitTextToSize(String(cell ?? "-"), columnWidths[index] - 4),
          );
          const rowHeight = Math.max(8, ...cellLines.map((lines) => lines.length * 4 + 4));
          if (cursorY + rowHeight > pageHeight - margin) {
            pdf.addPage();
            cursorY = margin;
            drawHeader();
          }

          let x = margin;
          cellLines.forEach((lines, index) => {
            pdf.setFillColor(rowIndex % 2 === 0 ? 249 : 243, rowIndex % 2 === 0 ? 250 : 244, rowIndex % 2 === 0 ? 251 : 246);
            pdf.rect(x, cursorY, columnWidths[index], rowHeight, "F");
            pdf.setDrawColor(229, 231, 235);
            pdf.rect(x, cursorY, columnWidths[index], rowHeight);
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(8);
            pdf.setTextColor(31, 41, 55);
            pdf.text(lines, x + 2, cursorY + 4.5);
            x += columnWidths[index];
          });
          cursorY += rowHeight;
        });
        cursorY += 8;
      };

      drawTable("Core Quality Metrics", ["Metric", "Result"], [
        ["Test Coverage", `${stats.coverage}%`],
        ["Execution Success Rate", `${stats.successRate}%`],
        ["Critical Bugs", String(stats.criticalIssues)],
      ], [contentWidth * 0.68, contentWidth * 0.32]);

      drawTable("Active Blocking Defects", ["ID", "Title", "Severity", "Status"],
        blockingBugs.map((bug) => [bug.id, bug.title, bug.severity, bug.status]),
        [25, contentWidth - 25 - 27 - 30, 27, 30]);

      drawTable("Failed Tests", ["ID", "Title", "Module"],
        failedTests.map((test) => [test.id, test.title, test.module]),
        [28, contentWidth - 28 - 42, 42]);

      ensureSpace(28);
      const signatureWidth = (contentWidth - 20) / 2;
      ["QA Lead", "Engineering Manager"].forEach((role, index) => {
        const x = margin + index * (signatureWidth + 20);
        pdf.setDrawColor(75, 85, 99);
        pdf.line(x, cursorY + 10, x + signatureWidth, cursorY + 10);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(75, 85, 99);
        pdf.text(role, x, cursorY + 15);
      });

      const fileName = `Executive_Quality_Audit_Project_${selectedProject?.project_id || "ID"}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error("Failed to generate PDF:", err);
      toast.error("Failed to generate PDF document.");
    } finally {
      setIsDownloading(false);
    }
  };

  const getScoreUI = () => {
    if (stats.readinessScore === 0 && stats.totalTests === 0) {
      return {
        color: "text-gray-500",
        badge: "bg-gray-500/10 text-gray-500 border-gray-500/20",
        panel: "border-gray-500/30 bg-gray-500/5",
        titleColor: "text-gray-500",
        icon: <Info size={18} />,
        status: "No Data - Testing Not Started",
        desc: "There are no test cases declared or executed yet. Start testing to generate a release score.",
      };
    } else if (stats.readinessScore >= 85) {
      return {
        color: "text-green-500",
        badge: "bg-green-500/10 text-green-500 border-green-500/20",
        panel: "border-green-500/30 bg-green-500/5",
        titleColor: "text-[#10b981]",
        icon: <CheckCircle size={18} />,
        status: "Ready for Release",
        desc: "Based on high test coverage, excellent success rate, and minimal critical defects, this release is safe.",
      };
    } else if (stats.readinessScore >= 70) {
      return {
        color: "text-orange-500",
        badge: "bg-orange-500/10 text-orange-500 border-orange-500/20",
        panel: "border-orange-500/30 bg-orange-500/5",
        titleColor: "text-orange-500",
        icon: <Info size={18} />,
        status: "Review Required",
        desc: "Test coverage or active bugs pose a moderate risk to the release. Proceed with stakeholder approval.",
      };
    }

    return {
      color: "text-red-500",
      badge: "bg-red-500/10 text-red-500 border-red-500/20",
      panel: "border-red-500/30 bg-red-500/5",
      titleColor: "text-red-500",
      icon: <XCircle size={18} />,
      status: "Blocked",
      desc: "Significant technical debt, failing tests, or critical bugs are blocking this release. Do not deploy.",
    };
  };

  const ui = getScoreUI();

  if (isLoading) {
    return (
      <div className="p-12 text-center text-gray-500 animate-pulse">
        Calculating Release Readiness...
      </div>
    );
  }

  return (
    <div
      ref={reportRef}
      className="px-8 pb-12 max-w-7xl mx-auto animate-in fade-in duration-500"
    >
      {/* Header and Download Button Section */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center w-full gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Release Scorer</h2>
          <p className="text-gray-500 text-sm mt-1">
            Evaluate release readiness with an objective quality assessment
          </p>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-3">
          <label htmlFor="release-sprint-filter" className="sr-only">
            Filter by sprint or release
          </label>
          <select
            id="release-sprint-filter"
            value={selectedSprint}
            onChange={(event) => setSelectedSprint(event.target.value)}
            disabled={!selectedProject?.project_id}
            className={`rounded-xl border ${borderColor} ${cardBg} px-4 py-2.5 text-sm outline-none focus:border-purple-500 disabled:opacity-50`}
          >
            <option value="all">All Sprints</option>
            {availableSprints.map((sprint) => (
              <option key={sprint} value={sprint}>
                {sprint}
              </option>
            ))}
          </select>
          <button
            id="download-pdf-btn"
            onClick={handleDownloadReport}
            disabled={isDownloading}
            className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm font-bold shadow-lg shadow-purple-500/20 transition-all cursor-pointer disabled:opacity-50"
          >
            {isDownloading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Generating PDF...
              </>
            ) : (
              <>
                <Download size={18} /> Download Release Report
              </>
            )}
          </button>
        </div>
      </div>

      {/* 1. HERO READINESS SCORE & AUTOMATION ROI GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Left: Release Readiness Score */}
        <div
          className={`lg:col-span-2 p-10 rounded-3xl border ${cardBg} ${borderColor} text-center relative overflow-hidden transition-colors`}
        >
          <div
            className="absolute -bottom-10 -right-10 text-gray-800/10"
            style={{ fontSize: "180px" }}
          >
            ⚡
          </div>
          <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
            Release Readiness Score
          </p>
          <h2
            className={`text-7xl font-extrabold tracking-tighter mb-4 ${ui.color}`}
          >
            {stats.readinessScore}/100
          </h2>
          <div
            className={`inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border font-bold text-sm mb-4 ${ui.badge}`}
          >
            {ui.icon} {ui.status}
          </div>
          <p className="text-sm text-gray-400 max-w-xl mx-auto leading-relaxed">
            {ui.desc}
          </p>
        </div>

        {/* Right: Automation ROI Card */}
        <div
          className={`lg:col-span-1 relative overflow-hidden rounded-3xl border backdrop-blur-xl p-8 flex flex-col justify-center items-center text-center transition-all duration-500 hover:scale-[1.02]
  ${
    darkMode
      ? "border-purple-500/40 bg-gradient-to-br from-[#1a1028]/90 via-[#120b1f]/90 to-[#09080f]/90 shadow-[0_0_45px_rgba(168,85,247,0.18)]"
      : "border-purple-300 bg-gradient-to-br from-white via-purple-50 to-indigo-50 shadow-xl"
  }`}
        >
          {/* Animated Glow */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -left-20 w-56 h-56 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-16 -right-16 w-44 h-44 bg-fuchsia-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>
          </div>

          {/* Grid Overlay */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.2) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />

          {/* Small Top Indicator */}
          <div className="relative z-10 flex items-center gap-2 mb-5">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>

            <p
              className={`text-xs font-bold uppercase tracking-[0.3em] ${
                darkMode ? "text-purple-300" : "text-purple-700"
              }`}
            >
              Automation ROI
            </p>
          </div>

          {/* Main Value */}
          <h2
            className={`relative z-10 text-6xl font-black tracking-tight drop-shadow-lg ${
              darkMode ? "text-purple-300" : "text-purple-600"
            }`}
          >
            {stats.timeSaved}
          </h2>

          {/* Subtitle */}
          <p
            className={`relative z-10 mt-3 text-sm font-semibold tracking-wide ${
              darkMode ? "text-purple-200" : "text-purple-700"
            }`}
          >
            QA Time Saved
          </p>

          {/* Divider */}
          <div
            className={`relative z-10 my-6 w-20 h-px ${
              darkMode
                ? "bg-gradient-to-r from-transparent via-purple-400 to-transparent"
                : "bg-gradient-to-r from-transparent via-purple-500 to-transparent"
            }`}
          />

          {/* Badge */}
          {stats.multiplier > 0 && (
            <div
              className={`relative z-10 inline-flex items-center gap-2 px-5 py-2 rounded-full border transition-all duration-300 hover:scale-105
      ${
        darkMode
          ? "border-yellow-400/40 bg-yellow-500/10 shadow-[0_0_20px_rgba(250,204,21,0.18)]"
          : "border-yellow-300 bg-yellow-100"
      }`}
            >
              <Zap size={15} className="text-yellow-400" fill="currentColor" />

              <span className="text-xs font-extrabold tracking-[0.15em] text-yellow-400">
                {stats.multiplier}× FASTER EXECUTION
              </span>
            </div>
          )}

          {/* Bottom Accent */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
        </div>
      </div>

      {/* 2. STATS GRID */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <ScorerStat
          title="Test Coverage"
          value={`${stats.coverage}%`}
          trend={stats.coverage >= 85 ? "Target Met" : "Below Target"}
          icon={<Scale />}
          color={stats.coverage >= 85 ? "green" : "orange"}
          darkMode={darkMode}
        />
        <ScorerStat
          title="Open Bugs"
          value={stats.openBugs.toString()}
          trend={stats.openBugs > 10 ? "Needs cleanup" : "Manageable"}
          icon={<Bug />}
          color={
            stats.openBugs === 0
              ? "green"
              : stats.openBugs > 10
                ? "orange"
                : "blue"
          }
          darkMode={darkMode}
        />
        <ScorerStat
          title="Automation AI Calls"
          value={stats.automationCalls.toString()}
          trend="Project executions"
          icon={<Zap />}
          color="blue"
          darkMode={darkMode}
        />
        <ScorerStat
          title="Critical Issues"
          value={stats.criticalIssues.toString()}
          trend={
            stats.criticalIssues > 0 ? "Requires attention" : "None Blocking"
          }
          icon={<AlertTriangle />}
          color={stats.criticalIssues > 0 ? "red" : "green"}
          darkMode={darkMode}
        />
      </div>

      {/* 3. CHARTS GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Donut Chart 1: Severity */}
        <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor}`}>
          <h3 className="font-bold text-base mb-8">
            Bug Severity Distribution
          </h3>
          <div className="flex items-center h-64 gap-8">
            <ResponsiveContainer width="50%" height="100%">
              <PieChart>
                <Pie
                  data={releasePieData}
                  innerRadius={70}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  stroke="none"
                  activeIndex={activeBugIndex}
                  activeShape={renderActiveShape}
                  onMouseEnter={(_, index) =>
                    onPieEnter(_, index, setActiveBugIndex)
                  }
                >
                  {releasePieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <ScorerLegend data={releasePieData} darkMode={darkMode} />
          </div>
        </div>

        {/* Donut Chart 2: Coverage Breakdown */}
        <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor}`}>
          <h3 className="font-bold text-base mb-8">Test Execution Breakdown</h3>
          <div className="flex items-center h-64 gap-8">
            <ResponsiveContainer width="50%" height="100%">
              <PieChart>
                <Pie
                  data={coveragePieData}
                  innerRadius={70}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  stroke="none"
                  activeIndex={activeCoverageIndex}
                  activeShape={renderActiveShapeCoverage}
                  onMouseEnter={(_, index) =>
                    onPieEnter(_, index, setActiveCoverageIndex)
                  }
                >
                  {coveragePieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <ScorerLegend data={coveragePieData} darkMode={darkMode} />
          </div>
        </div>
      </div>

      {/* 4. TREND GRAPH */}
      <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor} mb-8`}>
        <h3 className="font-bold text-base mb-8">
          Test Results Trend (Last 10 Days)
        </h3>
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={trendData}
              margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
            >
              <XAxis
                dataKey="name"
                stroke="#4b5563"
                fontSize={12}
                tickLine={false}
                axisLine={true}
                dy={10}
              />
              <YAxis
                stroke="#4b5563"
                fontSize={12}
                tickLine={false}
                axisLine={true}
                allowDecimals={false}
              />
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke={darkMode ? "#1f2937" : "#e5e7eb"}
                opacity={0.5}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ stroke: "#4b5563", strokeDasharray: "5 5" }}
              />
              <Legend iconType="circle" wrapperStyle={{ paddingTop: 20 }} />
              <Line
                type="monotone"
                name="Passed"
                dataKey="passed"
                stroke="#10b981"
                strokeWidth={2}
                dot={{
                  stroke: "#10b981",
                  strokeWidth: 2,
                  r: 4,
                  fill: darkMode ? "#161922" : "#fff",
                }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                name="Failed"
                dataKey="failed"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{
                  stroke: "#ef4444",
                  strokeWidth: 2,
                  r: 4,
                  fill: darkMode ? "#161922" : "#fff",
                }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 5. RECOMMENDATION PANEL */}
      <div className={`p-8 rounded-3xl border ${ui.panel}`}>
        <div className="flex items-center gap-4 mb-5">
          {ui.icon}
          <h3 className={`font-bold text-lg ${ui.titleColor}`}>
            Release Recommendation:{" "}
            {stats.readinessScore === 0 && stats.totalTests === 0
              ? "N/A"
              : stats.readinessScore >= 85
                ? "Go"
                : stats.readinessScore >= 70
                  ? "Review Required"
                  : "No-Go"}
          </h3>
        </div>
        <p
          className={`text-sm leading-relaxed mb-6 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
        >
          {ui.desc}
        </p>
        <ul className="space-y-3.5 text-xs font-medium">
          <RecItem
            label={
              stats.coverage >= 85
                ? `Test coverage excellent at ${stats.coverage}%`
                : `Test coverage is only ${stats.coverage}%. Try to reach 85%.`
            }
            check={stats.coverage >= 85}
            alert={stats.coverage < 85}
          />
          <RecItem
            label={
              stats.successRate >= 95
                ? `High test success rate (${stats.successRate}%)`
                : `Test success rate (${stats.successRate}%) indicates underlying stability issues.`
            }
            check={stats.successRate >= 95}
            alert={stats.successRate < 95}
          />
          <RecItem
            label={
              stats.criticalIssues > 0
                ? `${stats.criticalIssues} critical bug(s) require immediate attention before release.`
                : `No critical bugs blocking release.`
            }
            check={stats.criticalIssues === 0}
            alert={stats.criticalIssues > 0}
          />
          {stats.openBugs === 0 && <RecItem label="Zero open bugs!" check />}
        </ul>
      </div>
    </div>
  );
};

// Subcomponent for the recommendation list items
function RecItem({ label, check, alert }) {
  let icon = <CheckCircle size={15} className="text-green-500" />;
  let textColor = "text-green-600 dark:text-green-400";

  if (alert) {
    icon = <AlertTriangle size={16} className="text-orange-500" />;
    textColor = "text-orange-600 dark:text-orange-400 font-bold";
  }

  return (
    <li className={`flex items-start gap-2.5 ${textColor}`}>
      <div className="mt-0.5">{icon}</div> {label}
    </li>
  );
}
